/**
 * The full employee record, as a page rather than a slide-out drawer.
 *
 * Company Admin and Owner only (the route guard enforces it, and the API does
 * again). Unlike the drawer this shows the whole record — bank account, PAN,
 * full Aadhaar, UAN, ESIC — and every uploaded document, because for this
 * audience seeing the complete record is the point. Opening it is audited
 * server-side.
 *
 * Keyed by user id, matching the Users list row that links here. A user with
 * no employee record is the ordinary case for an owner or admin login and is
 * shown plainly, not as an error.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Loader2, Lock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import { titleCaseName } from '../../lib/titleCase';
import { placementStatusLabel } from '../../lib/statusLabels';
import type {
  EmployeeFullDetail,
  Office,
  Page,
  ProfileReviewStatus,
  ShiftPolicy,
  StoredDocument,
} from '../../types';

const STATUS_TONE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  on_notice: 'bg-amber-100 text-amber-700',
  offboarded: 'bg-gray-100 text-gray-600',
};

const REVIEW_TONE: Record<ProfileReviewStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
};

const DOC_LABELS: Record<string, string> = {
  profile_photo: 'Profile photo',
  aadhaar_front: 'Aadhaar — front',
  aadhaar_back: 'Aadhaar — back',
  pan_card: 'PAN card',
  education_certificate: 'Certificate scan',
  id_proof: 'ID proof',
  address_proof: 'Address proof',
  bank_proof: 'Bank proof',
  offer_letter: 'Offer letter',
  experience_letter: 'Experience letter',
  resume: 'Resume',
  other: 'Document',
};

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-40 shrink-0 text-xs text-gray-500">{label}</dt>
      <dd className={`min-w-0 flex-1 break-words text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-gray-300">—</span>}
      </dd>
    </div>
  );
}

/** One document — images inline, everything else a download. */
function DocumentTile({ doc }: { doc: StoredDocument }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = doc.contentType.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    let stale = false;
    let objectUrl: string | null = null;
    void client
      .get(`/api/v1/documents/${doc.id}/download`, { responseType: 'blob' })
      .then((res) => {
        if (stale) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.id, isImage]);

  const download = async () => {
    try {
      const res = await client.get(`/api/v1/documents/${doc.id}/download`, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = doc.filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not download that file'));
    }
  };

  return (
    <div className="rounded-lg p-2 ring-1 ring-gray-200">
      <p className="mb-1 text-xs font-medium text-gray-700">
        {DOC_LABELS[doc.docType] ?? doc.docType}
      </p>
      {isImage ? (
        url ? (
          <button type="button" onClick={() => void download()} className="block w-full">
            <img
              src={url}
              alt={DOC_LABELS[doc.docType] ?? doc.docType}
              className="max-h-56 w-full rounded object-contain ring-1 ring-gray-100"
            />
          </button>
        ) : (
          <div className="flex h-32 items-center justify-center text-gray-300">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )
      ) : (
        <button type="button" className="btn-ghost text-xs" onClick={() => void download()}>
          <FileText className="h-3.5 w-3.5" />
          {doc.filename}
        </button>
      )}
    </div>
  );
}

function AssignSection({ person }: { person: EmployeeFullDetail }) {
  const queryClient = useQueryClient();
  const [assigning, setAssigning] = useState(false);
  const [policyId, setPolicyId] = useState(person.shiftPolicyId ?? '');
  const [officeId, setOfficeId] = useState(person.officeId ?? '');

  const { data: policies } = useQuery<Page<ShiftPolicy>>({
    queryKey: ['shift-policies', 'assignable'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/shift-policies', { params: { limit: 200 } })).data,
    enabled: assigning,
  });
  const { data: offices } = useQuery<Page<Office>>({
    queryKey: ['offices', 'assignable'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/offices', { params: { limit: 200 } })).data,
    enabled: assigning,
  });

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.patch(`/api/v1/admin/employees/${person.id}`, body)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee-full', person.userId] });
      setAssigning(false);
      toast.success('Saved');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not save that')),
  });

  const myPolicies = (policies?.items ?? []).filter((p) => p.companyId === person.companyId);
  const myOffices = (offices?.items ?? []).filter((o) => o.companyId === person.companyId);

  if (!assigning) {
    return (
      <button type="button" className="btn-ghost text-xs" onClick={() => setAssigning(true)}>
        <Pencil className="h-3.5 w-3.5" />
        {person.shiftPolicyName ? 'Change shift or workplace' : 'Assign shift and workplace'}
      </button>
    );
  }

  return (
    <form
      className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({ shiftPolicyId: policyId || null, officeId: officeId || null });
      }}
    >
      <label className="block text-xs text-gray-600">
        Shift Policy
        <select className="input mt-1" value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
          <option value="">{myPolicies.length ? 'Not assigned' : 'None available'}</option>
          {myPolicies.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>
      <label className="block text-xs text-gray-600">
        Workplace
        <select className="input mt-1" value={officeId} onChange={(e) => setOfficeId(e.target.value)}>
          <option value="">{myOffices.length ? 'Not assigned' : 'None available'}</option>
          {myOffices.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </label>
      {(!policies || !offices) && (
        <div className="flex justify-center py-1 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}
      <div className="flex gap-2">
        <button type="submit" className="btn-primary px-2.5 py-1 text-xs" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Save
        </button>
        <button type="button" className="btn-ghost px-2.5 py-1 text-xs" onClick={() => setAssigning(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function EmployeeDetailPage() {
  const { userId = '' } = useParams();
  const navigate = useNavigate();

  const detail = useQuery<EmployeeFullDetail>({
    queryKey: ['employee-full', userId],
    queryFn: async () =>
      (await client.get(`/api/v1/admin/employees/by-user/${userId}/full`)).data,
    retry: false,
  });

  const back = (
    <button
      type="button"
      className="btn-ghost text-sm"
      onClick={() => navigate('/admin/users')}
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Users
    </button>
  );

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        {back}
        <div className="flex justify-center py-16 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="space-y-4">
        {back}
        <p className="card text-sm text-gray-500">
          No employee profile is on file for this person.
        </p>
      </div>
    );
  }

  const p = detail.data;
  const photo = p.documents.find((d) => d.docType === 'profile_photo');

  return (
    <div className="space-y-4">
      {back}

      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {photo ? (
              <PhotoAvatar docId={photo.id} />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gray-50 text-[11px] text-gray-400 ring-1 ring-gray-200">
                No photo
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900">{titleCaseName(p.fullName) ?? p.employeeCode}</h1>
              <p className="mt-0.5 text-xs text-gray-500">
                {[p.designation, p.department, p.employeeCode].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`badge ${STATUS_TONE[p.status] ?? 'bg-gray-100 text-gray-600'}`}>
              {placementStatusLabel(p.status)}
            </span>
            {p.profileStatus && (
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${REVIEW_TONE[p.profileStatus]}`}>
                {p.profileStatus === 'rejected' ? 'sent back' : p.profileStatus}
              </span>
            )}
          </div>
        </div>
        {p.anonymisedAt && (
          <p className="mt-2 rounded bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            Personal data for this record was erased on {p.anonymisedAt.slice(0, 10)}.
          </p>
        )}
        {p.profileStatus === 'rejected' && p.profileDecisionNote && (
          <p className="mt-2 rounded bg-rose-50 px-2.5 py-2 text-xs text-rose-800">
            <span className="font-medium">Sent back:</span> {p.profileDecisionNote}
          </p>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="card space-y-1">
          <h3 className="mb-1 text-sm font-semibold text-gray-900">Employment</h3>
          <dl className="divide-y divide-gray-100">
            <Row label="Designation" value={p.designation} />
            <Row label="Department" value={p.department} />
            <Row label="Reports To" value={titleCaseName(p.reportsToName)} />
            <Row label="Joined" value={p.dateOfJoining} />
            {p.dateOfExit && <Row label="Left" value={p.dateOfExit} />}
            <Row label="Shift" value={p.shiftPolicyName} />
            <Row label="Workplace" value={p.officeName} />
          </dl>
          <div className="pt-2">
            <AssignSection person={p} />
          </div>
        </section>

        <section className="card space-y-1">
          <h3 className="mb-1 text-sm font-semibold text-gray-900">Personal &amp; Contact</h3>
          <dl className="divide-y divide-gray-100">
            <Row label="Work email" value={p.email} />
            <Row label="Personal email" value={p.personalEmail} />
            <Row label="Date of birth" value={p.dateOfBirth} />
            <Row label="Blood group" value={p.bloodGroup} />
            <Row label="Emergency contact" value={p.emergencyContact} />
            <Row label="Address" value={p.address} />
          </dl>
        </section>
      </div>

      <section className="card space-y-1">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <Lock className="h-4 w-4 text-indigo-600" />
          Bank &amp; Statutory
        </h3>
        <dl className="grid gap-x-6 sm:grid-cols-2">
          <Row label="Bank name" value={p.bankName} />
          <Row label="IFSC" value={p.bankIfsc} mono />
          <Row label="Account number" value={p.bankAccountNumber} mono />
          <Row label="PAN" value={p.pan} mono />
          <Row label="Aadhaar" value={p.aadhaarNumber} mono />
          <Row label="UAN" value={p.uan} mono />
          <Row label="ESIC" value={p.esicNumber} mono />
        </dl>
      </section>

      <section className="card">
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Documents</h3>
        {p.documents.length === 0 ? (
          <p className="text-xs text-gray-400">No documents uploaded.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {p.documents.map((doc) => (
              <DocumentTile key={doc.id} doc={doc} />
            ))}
          </div>
        )}
      </section>

      {p.certificates.length > 0 && (
        <section className="card">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Certificates</h3>
          <ul className="space-y-1">
            {p.certificates.map((c) => (
              <li key={c.id} className="text-sm text-gray-700">
                {c.name}
                <span className="text-xs text-gray-400">
                  {c.issuingOrganisation ? ` · ${c.issuingOrganisation}` : ''}
                  {c.scans.length ? ` · ${c.scans.length} scan(s)` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The header avatar, downloaded like any other document blob. */
function PhotoAvatar({ docId }: { docId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let stale = false;
    let objectUrl: string | null = null;
    void client
      .get(`/api/v1/documents/${docId}/download`, { responseType: 'blob' })
      .then((res) => {
        if (stale) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId]);

  if (!url) {
    return <div className="h-14 w-14 rounded-lg bg-gray-50 ring-1 ring-gray-200" />;
  }
  return <img src={url} alt="" className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200" />;
}
